import { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { formatLocalized } from '@/utils/dateFormat';
import { useTimezone } from '@/contexts/TimezoneContext';
import { generateCalendarDays, todayInTimezone } from '@/utils/calendar';
import { CalendarDayCell } from './CalendarDayCell';
import { CalendarNavigation } from './CalendarNavigation';
import { useCalendarPosts, useMultiCalendarPosts, usePrefetchAdjacentMonths } from '@/hooks/useCalendarPosts';
import { useCalendarNotesMulti } from '@/hooks/useCalendarNotes';
import { useCalendarHolidaysMulti } from '@/hooks/useCalendarHolidays';
import { Post } from '@/services/posts';
import { CalendarView } from '@/types/calendar';
import { CalendarNote } from '@/types/calendar-notes';
import { filterCampaignsForDay, getCampaignDayPosition, CampaignDayPosition, assignCampaignSlots } from '@/utils/campaignMatcher';
import { timeSync, recordSlowOp } from '@/utils/perfTelemetry';

/**
 * CalendarGrid Component
 *
 * Main calendar view with:
 * - Month navigation (prev/next buttons)
 * - View toggle (Month/Week/Day)
 * - 7-column grid (Sun-Sat)
 * - Day cells with post counts and status indicators
 * - Today highlight
 * - Platform filter option
 */

interface CalendarGridProps {
  /**
   * Current view (month/week/day)
   */
  view: CalendarView;

  /**
   * Callback when view changes
   */
  onViewChange: (view: CalendarView) => void;

  /**
   * Platform filter
   */
  platform?: string;

  /**
   * Calendar IDs to display (empty = show all)
   */
  calendarIds?: number[];

  /**
   * Callback when a date is selected
   */
  onDateSelect: (date: Date, posts: Post[]) => void;

  /**
   * Currently selected date
   */
  selectedDate: Date | null;

  /**
   * Callback when a post is rescheduled via drag-and-drop
   */
  onPostRescheduled?: (postId: number, newDate: Date) => void;

  /**
   * Whether a drag operation is currently active (from parent DndContext)
   */
  isDraggingActive?: boolean;

  /**
   * Click on campaign to edit
   */
  onCampaignClick?: (campaign: CalendarNote) => void;

  /**
   * Callback when add post button is clicked on a day
   */
  onAddPost?: (date: Date) => void;

  /**
   * Callback when add campaign button is clicked on a day
   */
  onAddCampaign?: (date: Date) => void;

  /**
   * Callback when add content pillar button is clicked on a day
   */
  onAddContentPillar?: (date: Date) => void;

  /**
   * Callback when a post thumbnail is clicked directly - opens editor without the day drawer
   */
  onPostClick?: (post: Post) => void;

  /**
   * Callback when a post delete action is triggered from the popover
   */
  onPostDelete?: (post: Post) => void;

  /**
   * Map of calendarId → { name, color } for tooltip display
   */
  calendarsById?: Map<number, { name: string; color: string }>;
}

export const CalendarGrid = ({
  view,
  onViewChange,
  platform,
  calendarIds = [],
  onDateSelect,
  selectedDate,
  isDraggingActive = false,
  onCampaignClick,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
  onPostClick,
  onPostDelete,
  calendarsById,
}: CalendarGridProps) => {
  const __renderStart = performance.now();
  useLayoutEffect(() => {
    recordSlowOp('render:CalendarGrid', performance.now() - __renderStart);
  });
  const { t } = useTranslation('calendar');
  const { timezone } = useTimezone();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = todayInTimezone(timezone);

  // Weekday labels (translatable)
  const WEEKDAYS = [
    t('calendar_weekday_mon'),
    t('calendar_weekday_tue'),
    t('calendar_weekday_wed'),
    t('calendar_weekday_thu'),
    t('calendar_weekday_fri'),
    t('calendar_weekday_sat'),
    t('calendar_weekday_sun'),
  ];

  // Format month for API call — derive yyyy-MM from the team-tz noon anchor
  const monthString = format(currentMonth, 'yyyy-MM');

  // Calculate displayed date range in team tz (for adjacent-month fetch decisions).
  // calendarDays uses noon-anchored team-tz dates; the grid boundaries are the
  // first and last cells of that array.
  const calendarDays = useMemo(
    () => generateCalendarDays(currentMonth, timezone),
    [currentMonth, timezone]
  );
  const gridFirstDay = calendarDays[0] ?? currentMonth;
  const gridLastDay = calendarDays[calendarDays.length - 1] ?? currentMonth;
  const prevMonthString = format(subMonths(currentMonth, 1), 'yyyy-MM');
  const nextMonthString = format(addMonths(currentMonth, 1), 'yyyy-MM');
  const needsPrevMonth = format(gridFirstDay, 'yyyy-MM') !== monthString;
  const needsNextMonth = format(gridLastDay, 'yyyy-MM') !== monthString;

  // Fetch calendar posts (use multi-calendar hook if calendar IDs provided)
  const shouldUseMultiCalendar = calendarIds && calendarIds.length > 0;

  // Primary month
  const multiCalendarResult = useMultiCalendarPosts({
    month: monthString,
    calendarIds: calendarIds || [],
    platform,
    enabled: shouldUseMultiCalendar,
  });

  const singleCalendarResult = useCalendarPosts({
    month: monthString,
    platform,
    enabled: !shouldUseMultiCalendar,
  });

  // Previous month (leading days)
  const multiCalendarPrev = useMultiCalendarPosts({
    month: prevMonthString,
    calendarIds: calendarIds || [],
    platform,
    enabled: shouldUseMultiCalendar && needsPrevMonth,
  });
  const singleCalendarPrev = useCalendarPosts({
    month: prevMonthString,
    platform,
    enabled: !shouldUseMultiCalendar && needsPrevMonth,
  });

  // Next month (trailing days)
  const multiCalendarNext = useMultiCalendarPosts({
    month: nextMonthString,
    calendarIds: calendarIds || [],
    platform,
    enabled: shouldUseMultiCalendar && needsNextMonth,
  });
  const singleCalendarNext = useCalendarPosts({
    month: nextMonthString,
    platform,
    enabled: !shouldUseMultiCalendar && needsNextMonth,
  });

  // Merge results from primary + adjacent months
  const primaryResult = shouldUseMultiCalendar ? multiCalendarResult : singleCalendarResult;
  const prevResult = shouldUseMultiCalendar ? multiCalendarPrev : singleCalendarPrev;
  const nextResult = shouldUseMultiCalendar ? multiCalendarNext : singleCalendarNext;

  const isLoading = primaryResult.isLoading;
  const isError = primaryResult.isError;
  const error = primaryResult.error;

  const refetch = () => {
    primaryResult.refetch();
    if (needsPrevMonth) prevResult.refetch();
    if (needsNextMonth) nextResult.refetch();
  };

  const getPostsForDate = (date: string): Post[] => {
    const primary = primaryResult.getPostsForDate(date);
    if (primary.length > 0) return primary;
    if (needsPrevMonth) {
      const prev = prevResult.getPostsForDate(date);
      if (prev.length > 0) return prev;
    }
    if (needsNextMonth) {
      const next = nextResult.getPostsForDate(date);
      if (next.length > 0) return next;
    }
    return primary;
  };

  // Prefetch adjacent months for smoother navigation
  const { prefetch } = usePrefetchAdjacentMonths(monthString);

  // Prefetch when month changes
  useEffect(() => {
    prefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthString]);

  // Calculate month boundaries for campaigns and holidays
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Fetch campaigns/notes from ALL selected calendars (merged)
  const { data: campaigns } = useCalendarNotesMulti(
    calendarIds ?? [],
    format(monthStart, 'yyyy-MM-dd'),
    format(monthEnd, 'yyyy-MM-dd')
  );

  // Fetch holidays from ALL selected calendars (merged)
  const { data: holidays } = useCalendarHolidaysMulti(
    calendarIds ?? [],
    format(monthStart, 'yyyy-MM-dd'),
    format(monthEnd, 'yyyy-MM-dd')
  );

  /**
   * Navigate to previous month
   */
  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  /**
   * Navigate to next month
   */
  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  /**
   * Navigate to current month
   */
  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  /**
   * Handle date click - only opens drawer if there's content to show
   */
  const handleDateClick = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    const posts = getPostsForDate(dateString);

    // Check if there are campaigns for this date
    const dayCampaigns = filterCampaignsForDay(campaigns, date);

    // Check if there are holidays for this date
    const dayHolidays = holidays?.filter((h) =>
      isSameDay(new Date(h.holidayDate), date)
    ) || [];

    // Only open the drawer if there's content to show
    const hasContent = posts.length > 0 || dayCampaigns.length > 0 || dayHolidays.length > 0;

    if (hasContent) {
      onDateSelect(date, posts);
    }
  };

  // Compute stable slot indices for campaigns (like Google Calendar lane allocation)
  const campaignSlots = useMemo(
    () => timeSync('assignCampaignSlots', () => assignCampaignSlots(campaigns, calendarDays)),
    [campaigns, calendarDays]
  );

  /**
   * Get total posts for current month
   */
  return (
      <div className="bg-white relative flex flex-col">
        {/* Unified Calendar Navigation - Show for all views */}
        {view === 'month' && (
          <CalendarNavigation
            view={view}
            onViewChange={onViewChange}
            currentDate={currentMonth}
            onPrev={handlePrevMonth}
            onNext={handleNextMonth}
            onToday={handleToday}
            title={formatLocalized(currentMonth, 'MMMM yyyy')}
            hideTodayButton={isSameMonth(currentMonth, today)}
            onRefresh={refetch}
            isLoading={isLoading}
          />
        )}


      {/* Error State */}
      {isError && (
        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
            <Icon name={ICONS.WARNING} size="md" color="text-red-500" ariaHidden />
          </div>
          <p className="text-red-600 mb-4">
            {error?.message || t('calendar_error_load')}
          </p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            {t('calendar_try_again')}
          </Button>
        </div>
      )}

      {/* Calendar Grid */}
      {!isError && (
        <>
          {/* Month View - CalendarGrid only handles month view now */}
          {view === 'month' && (
            <>
              {/* Weekday Headers - Fixed */}
              <div className="px-2 sm:px-4 pt-2 bg-white sticky top-[calc(4.1rem+44px+48px)] z-10">
                <div className="grid grid-cols-7">
                  {WEEKDAYS.map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs sm:text-sm font-semibold text-gray-500 py-2"
                    >
                      <span className="hidden sm:inline">{day}</span>
                      <span className="sm:hidden">{day.charAt(0)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Days Grid - Scrollable */}
              <div className="px-2 sm:px-4 pb-2">
                <div
                  className={cn(
                    'grid grid-cols-7 gap-1 sm:gap-2',
                    isLoading && 'opacity-50'
                  )}
                >
                  {calendarDays.map((day, index) => {
                    const dateString = format(day, 'yyyy-MM-dd');
                    const isCurrentMonth = dateString.startsWith(monthString);
                    const isToday = isSameDay(day, today);
                    const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                    const posts = getPostsForDate(dateString);
                    const postCount = posts.length;

                    // Filter campaigns for this day (handles recurring campaigns)
                    const dayCampaigns = filterCampaignsForDay(campaigns, day);

                    // Compute position of each campaign on this day
                    const campaignPositions = new Map<number, CampaignDayPosition>();
                    for (const c of dayCampaigns) {
                      campaignPositions.set(c.id, getCampaignDayPosition(c, day));
                    }

                    // Filter holidays for this day
                    const dayHolidays = holidays?.filter((h) =>
                      isSameDay(new Date(h.holidayDate), day)
                    );

                    return (
                      <CalendarDayCell
                        key={index}
                        date={day}
                        isCurrentMonth={isCurrentMonth}
                        isToday={isToday}
                        isSelected={isSelected}
                        postCount={postCount}
                        posts={posts}
                        campaigns={dayCampaigns}
                        campaignPositions={campaignPositions}
                        campaignSlots={campaignSlots}
                        holidays={dayHolidays}
                        onClick={handleDateClick}
                        isDraggingActive={isDraggingActive}
                        onCampaignClick={onCampaignClick}
                        onAddPost={onAddPost}
                        onAddCampaign={onAddCampaign}
                        onAddContentPillar={onAddContentPillar}
                        onPostClick={onPostClick}
                        onPostDelete={onPostDelete}
                        calendarsById={calendarsById}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-xl z-10">
          <div className="flex items-center gap-2 text-gray-600">
            <Icon name={ICONS.LOADER} size="md" className="animate-spin" ariaHidden />
            <span>{t('calendar_loading')}</span>
          </div>
        </div>
      )}
      </div>
  );
};
